import { Class, HeatsTeam, Result, Team } from '@vote4dance/judging-schema';
import { ClassId, CompetitionId, JudgeId, RoundId, TeamId } from '@vote4dance/judging-types';
import { StreamSource } from "../hooks/streams";

export type TeamItem = Team
& Pick<Class, 'class_label' | 'class_name'>
& {
    team_starting_in: string
}

export function TeamSource(competition_id: CompetitionId) {
    return new StreamSource<TeamItem[]>(`/teams/${competition_id}`);
}

export function TeamItemSource(competition_id: CompetitionId, team_id: TeamId) {
    return new StreamSource<TeamItem>(`/teams/${competition_id}/${team_id}`);
}

export type HeatsTeamItem = HeatsTeam
& Pick<Team, 'team_name_1' | 'team_name_2' | 'team_name_3' | 'team_start_number' | 'team_cancelled' | 'team_disqualified' | 'team_org' | 'team_org_full' | 'team_country' | 'team_show_name' | 'team_year_1' | 'team_year_2' | 'team_year_3' | 'team_image_id' >
& {
    heats_team_sent: string | null,
    round_target_label: string | null
}

type HeatsTeamItemCompanion = {
    getFullName(team: HeatsTeamItem): string;
    getFullNameWithOrg(team: HeatsTeamItem): string;
}

type TeamItemCompanion = {
    getFullName(team: TeamItem): string;
    getFullNameWithOrg(team: TeamItem): string;
}

const getFullName = (team: HeatsTeamItem | TeamItem): string => {
    let  name = "";

    if(team.team_name_1) {
        name += team.team_name_1;
    }

    if(team.team_name_2 && team.team_name_3) {
        name += ', ';
    }  else if(team.team_name_2 && ! team.team_name_3) {
        name += ' & ';
    }

    if(team.team_name_2) {
        name += team.team_name_2;
    }

    if(team.team_name_3) {
        name += ' & ' + team.team_name_3;
    }
    return name;
}

const getFullNameWithOrg = (team: HeatsTeamItem | TeamItem): string => {
    let name = getFullName(team);
    if(team.team_org) {
        name += ' - (';
        name += team.team_org;
        name += ')';
    }
    return name;
}

export const HeatsTeamItem: HeatsTeamItemCompanion = {
    getFullName: getFullName,
    getFullNameWithOrg: getFullNameWithOrg
}

export const TeamItem: TeamItemCompanion = {
    getFullName: getFullName,
    getFullNameWithOrg: getFullNameWithOrg
}

export function HeatsTeamSource(round_id: RoundId, heat_number: number) {
    return new StreamSource<HeatsTeamItem[]>(`/teamheat/${round_id}/${heat_number}`);
}

export function HeatsTeamLineupSource(round_id: RoundId, heat_number: number) {
    return new StreamSource<HeatsTeamItem[]>(`/teamheatlineup/${round_id}/${heat_number}`);
}

export function HeatsTeamResultsSource(round_id: RoundId, heat_number: number, judge_id: JudgeId) {
    return new StreamSource<Result[]>(`/heatsteamresults/${round_id}/${heat_number}/${judge_id}`);
}

export function RoundTeamSource(round_id: RoundId) {
    return new StreamSource<HeatsTeamItem[]>(`/teamround/${round_id}`);
}

export function ClassTeamSource(class_id: ClassId) {
    return new StreamSource<TeamItem[]>(`/class-teams/${class_id}`);
}

export function ChildTeamSource(competition_id: CompetitionId, team_id: TeamId) {
    return new StreamSource<TeamItem[]>(`/child-teams/${competition_id}/${team_id}`);
}

export function CandidatesSource(class_id: ClassId, role: 'leader' | 'follower') {
    return new StreamSource<TeamItem[]>(`/partnering/candidates/${class_id}/${role}`);
}

export type CompetitionOrganizationItem = { org: string, org_name: string | null };

export function CompetitionOrganizationsSource(competition_id: CompetitionId) {
    return new StreamSource<CompetitionOrganizationItem[]>(`/competition-organizations/${competition_id}`);
}
